import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ApiRestService } from '../api-rest/api-rest.service';

@Injectable({
  providedIn: 'root',
})
export class ApiHappylandService {
  environment = environment.endpoints.status;
  envSecure = environment.endpoints.secure;
  envRegister = environment.endpoints.status;
  timezoneOffSet = new Date().getTimezoneOffset();
  private urlReturnTo = environment.endpoints.returnTo;

  constructor(private api: ApiRestService) {}

  async requestTokenJWT(card: string): Promise<HttpResponse<{ code: number; status: boolean; token: string }>> {
    try {
      const headers = new HttpHeaders().set('Content-Type', 'application/json');
      const params = {
        account: card,
        src: environment.endpoints.returnTo,
      };

      const response = await this.api.postQuery(`${this.envSecure}/auth/jwt`, headers, params);
      return Promise.resolve(response);
    } catch (errCritical) {
      // console.error(errCritical);
      return Promise.reject(errCritical);
    }
  }

  async checkIfCardIsEnable(card: string): Promise<HttpResponse<any>> {
    const bearerAuthorization = sessionStorage.getItem('token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

    const params = {
      account: card,
    };

    const response = await this.api.postQuery(`${this.environment}/cards/balance`, headers, params);
    return Promise.resolve(response);
  }

  async getCardID(url: string): Promise<HttpResponse<any>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const response = await this.api.getQuery(url, headers);
      return Promise.resolve(response);
    } catch (errCritical) {
      // console.error(errCritical);
      return Promise.reject(errCritical);
    }
  }
  /**
   *
   * @param dni
   * @param email
   * @param corpID
   * @param promotionUid
   * @param transactionToken
   * @param timestamp
   * @param internalSessionToken - UID de sesión de tracking (para vincular con transacción)
   * @returns
   */
  async generateInvoiceAndCharge(
    dni: string,
    email: string,
    corpID: string,
    promotionUid: string,
    transactionToken: string,
    timestamp: number,
    internalSessionToken?: string
  ): Promise<HttpResponse<any>> {
    const maxRetries = 30; // 60 seconds max
    const delay = 2000;

    const bearerAuthorization = sessionStorage.getItem('token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

    const params = {
      timestamp: timestamp,
      token: transactionToken,
    };

    for (let i = 0; i < maxRetries; i++) {
      try {
        // Cambiado de /charge a /validate para solo validar el estado
        const response = await this.api.postQuery(`${this.envSecure}/validate`, headers, params);

        if (response && response.body) {
          if (response.body.status === true) {
            return Promise.resolve(response);
          }

          const statusMessage = response.body.message;

          if (statusMessage === 'REFUNDING') {
            return Promise.resolve(response);
          }

          if (['AUTHORIZED', 'RELOADING_INVOICE', 'INVOICE_FAILED', 'RELOAD_FAILED'].includes(statusMessage)) {
            // Continue polling
          } else {
            return Promise.resolve(response);
          }
        }

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));
      } catch (errCritical) {
        // console.error(errCritical);
        return Promise.reject(errCritical);
      }
    }
    return Promise.reject({ status: 408, message: 'Timeout' });
  }

  /**
   * Registra al usuario en Intercard y le asocia la tarjeta.
   * @param userInformation Datos del usuario
   * @param corpID ID corporativo
   * @param account Número de tarjeta
   * @param checkPromotions Preferencia explícita del usuario sobre promociones;
   * si no se informa, api-status conserva el valor existente
   * @param skipProfileUpdate Evita actualizar los datos si el usuario ya existe en Intercard
   */
  async registerAndAssignCard(
    userInformation: { email: string; firstName?: string; lastName?: string; phoneNumber?: string; rut?: string; commune?: string; birthDate?: string },
    corpID: number,
    account: string,
    checkPromotions?: boolean,
    skipProfileUpdate: boolean = false
  ): Promise<HttpResponse<any>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const params = {
        email: userInformation.email,
        firstName: userInformation.firstName,
        lastName: userInformation.lastName,
        phoneNumber: userInformation.phoneNumber ? String(userInformation.phoneNumber) : undefined,
        rut: userInformation.rut,
        commune: userInformation.commune,
        birthDate: userInformation.birthDate,
        account: account,
        ...(typeof checkPromotions === 'boolean' ? { isPromotionRequested: checkPromotions } : {}),
        skipProfileUpdate: skipProfileUpdate,
      };

      // Se utiliza el endpoint de loyalty en api-status (4201) en lugar de api-register (4203)
      const response = await this.api.postQuery(`${this.environment}/loyalty/enroll`, headers, params);
      return Promise.resolve(response);
    } catch (errCritical) {
      // console.error(errCritical);
      return Promise.reject(errCritical);
    }
  }

  /**
   * Actualiza los datos de un usuario ya registrado en Intercard.
   * El email no es editable, por lo que no se envía.
   * @param account Número de tarjeta del usuario
   * @param userInformation Datos editables del perfil
   */
  async updateLoyaltyProfile(
    account: string,
    userInformation: { firstName?: string; lastName?: string; phoneNumber?: string; rut?: string; commune?: string; birthDate?: string }
  ): Promise<HttpResponse<any>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const params = {
        account: String(account),
        firstName: userInformation.firstName,
        lastName: userInformation.lastName,
        phoneNumber: userInformation.phoneNumber ? String(userInformation.phoneNumber) : undefined,
        rut: userInformation.rut,
        commune: userInformation.commune,
        birthDate: userInformation.birthDate,
      };

      const response = await this.api.putQuery(`${this.environment}/loyalty/profile`, headers, params);
      return Promise.resolve(response);
    } catch (errCritical) {
      return Promise.reject(errCritical);
    }
  }

  async validateTransactionStatus(promotionUid: string, transactionToken: string, timestamp: number): Promise<HttpResponse<any>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const params = {
        token: transactionToken,
        timestamp: timestamp,
      };

      const response = await this.api.postQuery(`${this.envSecure}/validate`, headers, params);
      return Promise.resolve(response);
    } catch (errCritical) {
      return Promise.reject(errCritical);
    }
  }

  async requestHistoryInformationByCard(account: string | number): Promise<HttpResponse<any>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const params = {
        account: String(account),
        utcDiff: -this.timezoneOffSet,
      };

      const response = await this.api.postQuery(`${this.environment}/cards/history`, headers, params);
      return Promise.resolve(response);
    } catch (errCritical) {
      // console.error(errCritical);
      return Promise.reject(errCritical);
    }
  }

  /**
   * Obtener la configuración personalizada del usuario
   * @param settings
   * @returns
   */
  async verifyIfUserHaveOffers(account: string | number): Promise<HttpResponse<any>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const params = {
        account: String(account),
      };

      const response = await this.api.postQuery(`${this.environment}/segments/evaluate`, headers, params);
      return Promise.resolve(response);
    } catch (errCritical) {
      return Promise.reject(errCritical);
    }
  }

  /**
   * Obtiene la configuración del web-client desde la API REST
   * Migración desde AppSync a REST API
   * @returns Configuración del web-client (límites de tiempo, etc.)
   */
  async getWebClientSettings(): Promise<
    HttpResponse<{
      status: boolean;
      message: string;
      data: { uid: string; environment: string | null; minAccountTimeLimit: number; maxAccountTimeLimit: number } | null;
    }>
  > {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const response = await this.api.getQuery(`${this.environment}/settings/web-client`, headers);
      return Promise.resolve(response);
    } catch (errCritical) {
      return Promise.reject(errCritical);
    }
  }

  /**
   * Actualiza la configuración de ofertas especiales de la tarjeta
   * Migración desde AppSync a REST API
   * @param account Número de cuenta/tarjeta
   * @param showSpecialOffer Indica si se deben mostrar ofertas segmentadas
   * @returns Configuración actualizada
   */
  async updateCardSettings(
    account: number,
    showSpecialOffer: boolean
  ): Promise<HttpResponse<{ status: boolean; message: string; data: { account: number; showSpecialOffer: boolean; segmentRef: string | null } }>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const params = {
        account: account,
        showSpecialOffer: showSpecialOffer,
      };

      const response = await this.api.postQuery(`${this.environment}/cards/settings`, headers, params);
      return Promise.resolve(response);
    } catch (errCritical) {
      return Promise.reject(errCritical);
    }
  }

  /**
   * Canjear promoción con monto a pagar cero
   * Este endpoint unificado realiza todo el proceso de canje en una sola llamada
   * @param account Número de cuenta/tarjeta
   * @param promotionUid UID de la promoción
   * @param email Email del usuario
   * @param trackingToken Token de tracking opcional
   * @returns Respuesta con datos del canje completado
   */
  async redeemZeroAmountPromotion(
    account: number,
    promotionUid: string,
    email: string,
    trackingToken?: string
  ): Promise<HttpResponse<{ message: string; data: { url: string | null; folio: number; reload: boolean } }>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const params: Record<string, any> = {
        account: String(account),
        email: email,
        promotionUID: promotionUid,
        returnTo: this.urlReturnTo,
      };

      if (trackingToken) {
        params['trackingToken'] = trackingToken;
      }

      const response = await this.api.postQuery(`${this.envSecure}/segments/redeem`, headers, params);
      return Promise.resolve(response);
    } catch (errCritical) {
      return Promise.reject(errCritical);
    }
  }

  /**
   * @deprecated Use redeemZeroAmountPromotion instead - Este método ya no es necesario
   * El endpoint /segments/redeem es unificado y realiza todo el proceso en una sola llamada
   */
  async createNewTransactionWithPaymentAmountZero(
    account: number,
    promotionUid: string,
    email: string
  ): Promise<HttpResponse<{ message: string; data: { token: string; timestamp: number } }>> {
    console.warn('DEPRECATED: createNewTransactionWithPaymentAmountZero - Use redeemZeroAmountPromotion instead');
    // Redirigir al nuevo método para mantener compatibilidad temporal
    const response = await this.redeemZeroAmountPromotion(account, promotionUid, email);
    // Adaptar respuesta al formato esperado por el código legacy
    return {
      ...response,
      body: {
        message: response.body?.message || 'OFFER_REDEEM_SUCCESSFUL',
        data: {
          token: 'unified-redeem',
          timestamp: Date.now(),
        },
      },
    } as HttpResponse<{ message: string; data: { token: string; timestamp: number } }>;
  }

  /**
   * @deprecated Use redeemZeroAmountPromotion instead - Este método ya no es necesario
   * El endpoint /segments/redeem es unificado y realiza todo el proceso en una sola llamada
   */
  async requestReloadFromLastTransactionWithPaymentAmountZero(
    transactionUID: string,
    transactionTimestamp: number
  ): Promise<
    HttpResponse<{
      message: string;
      data: {
        url: string;
        folio: number;
        reload: any;
      };
    }>
  > {
    console.warn('DEPRECATED: requestReloadFromLastTransactionWithPaymentAmountZero - Already processed by redeemZeroAmountPromotion');
    // Este método ya no hace nada porque el proceso se completó en redeemZeroAmountPromotion
    // Retornar respuesta mock para mantener compatibilidad
    return {
      ok: true,
      status: 200,
      body: {
        message: 'OFFER_REDEEM_SUCCESSFUL',
        data: {
          url: '',
          folio: 0,
          reload: true,
        },
      },
    } as HttpResponse<{ message: string; data: { url: string; folio: number; reload: any } }>;
  }

  // ============================================================================
  // Métodos REST que reemplazan las operaciones GraphQL de AppSync
  // ============================================================================

  /**
   * Lista promociones usando REST API
   * Reemplaza la llamada GraphQL ListPromotions
   * @param filter Filtro opcional para las promociones
   * @param nextToken Token de paginación
   * @returns Lista de promociones
   */
  async listPromotions(filter?: Record<string, any>, nextToken?: string): Promise<HttpResponse<{ status: boolean; message: string; data: { items: any[]; nextToken?: string } }>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const params: Record<string, any> = {};
      if (filter) {
        params['filter'] = filter;
      }
      if (nextToken) {
        params['nextToken'] = nextToken;
      }

      const response = await this.api.postQuery(`${this.environment}/promotions/list`, headers, params);
      return Promise.resolve(response);
    } catch (errCritical) {
      return Promise.reject(errCritical);
    }
  }

  /**
   * Obtiene una promoción por UID usando REST API
   * Reemplaza la llamada GraphQL GetPromotions
   * @param uid UID de la promoción
   * @returns Datos de la promoción
   */
  async getPromotion(uid: string): Promise<HttpResponse<{ status: boolean; message: string; data: any }>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const response = await this.api.getQuery(`${this.environment}/promotions/${uid}`, headers);
      return Promise.resolve(response);
    } catch (errCritical) {
      return Promise.reject(errCritical);
    }
  }

  /**
   * Lista segmentos usando REST API
   * Reemplaza la llamada GraphQL ListSegments
   * @param filter Filtro opcional para los segmentos
   * @param nextToken Token de paginación
   * @returns Lista de segmentos
   */
  async listSegments(filter?: Record<string, any>, nextToken?: string): Promise<HttpResponse<{ status: boolean; message: string; data: { items: any[]; nextToken?: string } }>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const params: Record<string, any> = {};
      if (filter) {
        params['filter'] = filter;
      }
      if (nextToken) {
        params['nextToken'] = nextToken;
      }

      const response = await this.api.postQuery(`${this.environment}/segments/list`, headers, params);
      return Promise.resolve(response);
    } catch (errCritical) {
      return Promise.reject(errCritical);
    }
  }

  /**
   * Obtiene la configuración de una tarjeta usando REST API
   * Reemplaza la llamada GraphQL GetCardSettings
   * @param account Número de cuenta/tarjeta
   * @returns Configuración de la tarjeta
   */
  async getCardSettings(
    account: number
  ): Promise<HttpResponse<{ status: boolean; message: string; data: { account: number; showSpecialOffer: boolean; segmentRef: string | null } | null }>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const response = await this.api.getQuery(`${this.environment}/cards/settings/${account}`, headers);
      return Promise.resolve(response);
    } catch (errCritical) {
      return Promise.reject(errCritical);
    }
  }

  /**
   * Crea la configuración de una tarjeta usando REST API
   * Reemplaza la llamada GraphQL CreateCardSettings
   * @param settings Configuración a crear
   * @returns Configuración creada
   */
  async createCardSettings(settings: {
    account: number;
    showSpecialOffer?: boolean;
    segmentRef?: string;
  }): Promise<HttpResponse<{ status: boolean; message: string; data: { account: number; showSpecialOffer: boolean; segmentRef: string | null } }>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const response = await this.api.postQuery(`${this.environment}/cards/settings`, headers, settings);
      return Promise.resolve(response);
    } catch (errCritical) {
      return Promise.reject(errCritical);
    }
  }

  /**
   * Lista recompras usando REST API
   * Reemplaza la llamada GraphQL ListRepurchases
   * @param filter Filtro opcional para las recompras
   * @param nextToken Token de paginación
   * @returns Lista de recompras
   */
  async listRepurchases(filter?: Record<string, any>, nextToken?: string): Promise<HttpResponse<{ status: boolean; message: string; data: { items: any[]; nextToken?: string } }>> {
    try {
      const bearerAuthorization = sessionStorage.getItem('token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${bearerAuthorization}`).set('Content-Type', 'application/json');

      const params: Record<string, any> = {};
      if (filter) {
        params['filter'] = filter;
      }
      if (nextToken) {
        params['nextToken'] = nextToken;
      }

      const response = await this.api.postQuery(`${this.environment}/repurchases/list`, headers, params);
      return Promise.resolve(response);
    } catch (errCritical) {
      return Promise.reject(errCritical);
    }
  }
}
